import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { isQuietError, useLicApi, orgPath, seatReleasePath } from './licApi';
import {
  ACCENT,
  ConfirmDialog,
  CopyButton,
  EmptyRow,
  HEADING,
  LoadingBox,
  MUTED,
  Section,
  StatTile,
  StatusChip,
  ViewHeader,
  tableContainerSx,
  useCodeConfirmation,
} from './OrgCommon';
import { daysSince, daysUntil, formatDate, formatDateTime, formatRelative } from './orgFormat';

const STALE_DAYS = 7;
const WARNING = '#B26A00';

const InfoItem = ({ label, children }) => (
  <Box sx={{ minWidth: 160 }}>
    <Typography variant="body2" sx={{ color: MUTED }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: HEADING, fontWeight: 600, minHeight: 32 }}>
      {children}
    </Box>
  </Box>
);

/** Seat pool: totals, the org code to hand out, and who holds each seat (IT can revoke one). */
const LicensesView = ({ org, notify, onOrgChanged }) => {
  const { licFetch } = useLicApi();
  const { run, codeDialog } = useCodeConfirmation();
  const [seats, setSeats] = useState([]);
  const [seatCount, setSeatCount] = useState(org.seatCount);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [releasing, setReleasing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await licFetch(orgPath(org.id, '/seats'));
      setSeats(Array.isArray(result.seats) ? result.seats : []);
      if (typeof result.seatCount === 'number') {
        setSeatCount(result.seatCount);
      }
    } catch (loadError) {
      if (loadError.status !== 401) setError(loadError.message);
    }
  }, [licFetch, org.id]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([load(), onOrgChanged()]);
    setRefreshing(false);
  };

  const handleRelease = async () => {
    if (!releasing) return;
    setBusy(true);
    try {
      // Revoking an IT admin's license asks for a code emailed to you.
      await run((headers) => licFetch(seatReleasePath(org.id, releasing.email), { method: 'POST', headers }));
      notify(`Revoked ${releasing.email}'s license.`);
      setReleasing(null);
      await Promise.all([load(), onOrgChanged()]);
    } catch (releaseError) {
      if (releaseError.code === 'Cancelled') setReleasing(null);
      else if (!isQuietError(releaseError)) notify(releaseError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const total = typeof seatCount === 'number' ? seatCount : org.seatCount ?? 0;
  const inUse = loading ? org.seatsUsed ?? 0 : seats.length;
  const free = Math.max(0, total - inUse);
  const isPersonal = org.type === 'Personal';
  const expiryDays = daysUntil(org.expiresAtUtc);

  return (
    <Box>
      <ViewHeader title="Licenses" onRefresh={handleRefresh} refreshing={refreshing} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatTile label="Total" value={total} />
        <StatTile label="In use" value={inUse} color={ACCENT} />
        <StatTile label="Free" value={free} color={free === 0 ? '#B3261E' : '#1E7B34'} />
      </Box>

      <Section>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {!isPersonal && (
            <InfoItem label="Org code">
              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, color: HEADING }}>
                {org.orgCode || '—'}
              </Typography>
              {org.orgCode && <CopyButton value={org.orgCode} label="Copy org code" onCopied={() => notify('Org code copied.')} />}
            </InfoItem>
          )}
          {isPersonal && <InfoItem label="Owner">{org.ownerEmail || '—'}</InfoItem>}
          <InfoItem label="Expires">
            {org.expiresAtUtc ? formatDate(org.expiresAtUtc) : 'No expiry'}
            {expiryDays !== null && expiryDays >= 0 && expiryDays <= 30 && (
              <Typography component="span" variant="body2" sx={{ color: WARNING, ml: 1 }}>
                ({expiryDays === 0 ? 'today' : `in ${expiryDays} day${expiryDays === 1 ? '' : 's'}`})
              </Typography>
            )}
          </InfoItem>
          <InfoItem label="Status">
            <StatusChip org={org} />
          </InfoItem>
        </Box>
        {!isPersonal && (
          <Typography variant="body2" sx={{ color: MUTED, mt: 2 }}>
            People sign in to HUBAROC with their email and this org code. Each person can use one PC at a time.
          </Typography>
        )}
      </Section>

      <Typography variant="h6" sx={{ fontWeight: 600, color: HEADING, mb: 1 }}>
        Seats in use
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <LoadingBox />
      ) : (
        <Box sx={tableContainerSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Windows user</TableCell>
                <TableCell>Activated</TableCell>
                <TableCell>Last check</TableCell>
                <TableCell align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seats.length === 0 ? (
                <EmptyRow colSpan={6}>No one is using a license right now.</EmptyRow>
              ) : (
                seats.map((seat) => {
                  const lastCheck = seat.lastValidatedAtUtc || seat.activatedAtUtc;
                  const age = daysSince(lastCheck);
                  const stale = age !== null && age > STALE_DAYS;
                  return (
                    <TableRow key={`${seat.email}-${seat.deviceId || ''}`} hover>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{seat.email || '—'}</TableCell>
                      <TableCell>
                        <Tooltip title={[seat.deviceId, seat.pluginVersion && `Plugin ${seat.pluginVersion}`].filter(Boolean).join(' · ')}>
                          <span>{seat.deviceName || seat.deviceId || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{seat.windowsUser || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(seat.activatedAtUtc)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip
                          title={
                            stale
                              ? `${formatDateTime(lastCheck)}. Not checked in for over ${STALE_DAYS} days — the PC may be off or offline.`
                              : formatDateTime(lastCheck)
                          }
                        >
                          <span style={{ color: stale ? WARNING : undefined, fontWeight: stale ? 600 : undefined }}>
                            {formatRelative(lastCheck)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" color="error" sx={{ textTransform: 'none' }} disabled={!seat.email} onClick={() => setReleasing(seat)}>
                          Revoke license
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(releasing)}
        title="Revoke this license?"
        confirmLabel="Revoke license"
        busy={busy}
        onConfirm={handleRelease}
        onClose={() => setReleasing(null)}
      >
        {releasing
          ? `${releasing.email} will lose HUBAROC on ${releasing.deviceName || 'their PC'} at their next license check (within 24 h) and the license becomes free. They can take a free license again the next time they sign in.`
          : ''}
      </ConfirmDialog>
      {codeDialog}
    </Box>
  );
};

export default LicensesView;
